'use client';

import { useMemo, useState } from 'react';
import { HeadlineWrapper } from '@/components/headline-wrapper/HeadlineWrapper';
import { Link } from '@/components/link/Link';
import type { VisualStrategyProps } from '@/sections/visual-strategy/types';
import VisualStrategyChart from '@/sections/visual-strategy/VisualStrategyChart';
import VisualStrategyDetails from '@/sections/visual-strategy/VisualStrategyDetails';
import VisualStrategyPieCarousel from '@/sections/visual-strategy/VisualStrategyPieCarousel';
import {
  formatPercent,
  formatValue,
} from '@/sections/visual-strategy/visualStrategyUtils';

const VisualStrategy = ({
  JSONFORMAT,
  headlineWrapper,
}: VisualStrategyProps) => {
  const categories = JSONFORMAT?.categories ?? [];
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);

  const selectedCategory = useMemo(
    () =>
      categories.find((category) => category.id === activeCategoryId) ||
      categories[0],
    [activeCategoryId, categories],
  );

  if (!selectedCategory) {
    return null;
  }

  const details = selectedCategory.strategyDetails;
  const summary = selectedCategory.performanceSummary;
  const pies = selectedCategory.historicalPerformanceCharts?.pies ?? [];
  const line = selectedCategory.historicalPerformanceCharts?.line;
  const bar = selectedCategory.historicalPerformanceCharts?.bar;

  return (
    <section className="relative w-full overflow-hidden bg-brand-800 py-10 text-white lg:py-14">
      <div className="container mx-auto w-full">
        {headlineWrapper && (
          <HeadlineWrapper
            {...headlineWrapper}
            className="!bg-transparent !py-0"
            classNameDescription="!text-white"
          />
        )}

        <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:justify-between lg:gap-12">
          <div className="w-full lg:w-1/2">
            <h3 className="text-3xl font-semibold leading-tight">
              {selectedCategory.headline?.title}
            </h3>

            <p className="mt-4 text-sm leading-6 text-white">
              {selectedCategory.headline?.description}
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {categories.map((category, index) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => setActiveCategoryId(category.id)}
                  className={`min-w-14 rounded border px-4 py-2 text-xs transition ${
                    category.id === selectedCategory.id
                      ? 'border-brand-400 bg-brand-700 text-white'
                      : 'border-brand-600 bg-brand-700 text-white hover:border-brand-500'
                  }`}
                  aria-label={`Kategorie ${index + 1}`}
                >
                  {category.title}
                </button>
              ))}
            </div>

            {selectedCategory.headline?.link?.url && (
              <div className="mt-7">
                <Link
                  title={selectedCategory.headline.link.name}
                  variant="secondary"
                  className="w-full justify-center border-none bg-white !text-brand-800 hover:!text-brand-800"
                  href={selectedCategory.headline.link.url}
                />
              </div>
            )}

            <VisualStrategyDetails details={details} />
          </div>

          <div className="w-full lg:w-1/2 lg:border-l lg:border-white/20 lg:px-20">
            <VisualStrategyPieCarousel
              key={`pies-${selectedCategory.id}`}
              pies={pies}
            />

            <VisualStrategyChart
              key={`chart-${selectedCategory.id}`}
              line={line}
              bar={bar}
            />

            <article className="mt-8">
              <h4 className="text-base font-semibold">{summary.title}</h4>
              {summary.description && (
                <p className="mt-2 text-sm leading-6 text-white">
                  {summary.description}
                </p>
              )}

              <table className="mt-4 w-full border-collapse text-sm">
                <tbody>
                  {summary.metrics.map((metric) => (
                    <tr
                      key={metric.id}
                      className="border-b border-white/20 last:border-b-0"
                    >
                      <td className="py-2 text-white">{metric.label}</td>
                      <td className="py-2 text-right font-semibold">
                        {metric.unit === '%'
                          ? formatPercent(metric.value, {
                              // Volatility is a non-directional measure, so it
                              // must not carry a leading +/- sign; directional
                              // metrics (return, best/worst year) keep it.
                              signed: metric.id !== 'averageVolatility',
                            })
                          : formatValue(metric.value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VisualStrategy;
